// @ts-check
import { HiNotification } from '@/js/common/HINotificationSupport';
import { callObtenerDatosAdjuntosEntidadSeleccionada } from '@/js/services/traceability/APITraceability';
import { FILE_EXTENSIONS, getFileExtensionFromBase64Content } from '@/js/utils/file';
import { logError } from '@/js/utils/log';
import PDFIcon from '@/static/img/icon-pdf.png';
import { useSettingRef } from '@erpagroapp/settings';
import { f7 } from 'framework7-vue';
import { ref } from 'vue';

/** @import { Ref } from 'vue' */
/** @import { Imagen } from '@erpagroapp/types' */

/**
 * A composable that loads the attachments data for an entity, and provides methods to interact with them.
 */
export const useEntityAttachmentsData = () => {
  /** @type {Ref<Imagen[]>} */
  const attachments = ref([]);
  const isLoadingData = ref(false);

  const urlApp = useSettingRef('App.URLWebService');

  /**
   * Loads the attachments data.
   *
   * @param {number} entityId - The ID of the entity.
   * @param {string} entityType - The type of the entity.
   * @returns {Promise<boolean>} `true` if the data was loaded successfully, `false` otherwise.
   *
   * @example
   * loadAttachmentsData(entityId, 'PedidoVenta');
   */
  async function loadAttachmentsData(entityId, entityType) {
    isLoadingData.value = true;

    let successful = false;

    try {
      const data = await callObtenerDatosAdjuntosEntidadSeleccionada(entityId, entityType);

      if (data.Valor) {
        attachments.value = data.Mensaje ? JSON.parse(data.Mensaje) : '';
        successful = true;
      } else {
        HiNotification.Toast('No se han podido cargar los datos adjuntos');
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se han podido cargar los datos adjuntos');
    } finally {
      isLoadingData.value = false;
    }

    return successful;
  }

  /**
   * Opens a photo browser.
   *
   * @param {string[]} images - The images in base64.
   * @param {number} i - The index.
   * @returns {void}
   */
  function openPhotoBrowser(images, i) {
    const myPhotoBrowserPopup = f7.photoBrowser.create({
      photos: images,
      type: 'popup',
    });

    myPhotoBrowserPopup.open(i);
  }

  /**
   * Get the path of the linked attachment.
   *
   * @param {Imagen} attachment - The attachment.
   * @returns {string | undefined} The path of the linked attachment.
   */
  function getAttachmentLinkedUrl(attachment) {
    if (!urlApp.value) {
      return undefined;
    }

    const currentRoute = attachment.RutaImagenVinculada;
    if (!currentRoute) {
      return undefined;
    }

    const routesArray = currentRoute.split('wwwroot');
    if (routesArray.length < 1 || !routesArray[1]) {
      return undefined;
    }

    const hostWs = urlApp.value.substring(0, urlApp.value.lastIndexOf('/'));
    const nonFormattedUrl = `${hostWs}/${routesArray[1]}`;

    const url = nonFormattedUrl
      .replaceAll('\\', '/')
      .replaceAll(/(?<!:)\/+/g, '/');

    return url;
  }

  /**
   * Get the URL of the embedded attachment.
   *
   * @param {Imagen} attachment - The attachment.
   * @param {string} [fileExtension] - The file extension.
   * @returns {string | undefined} The URL of the embedded attachment.
   */
  function getAttachmentEmbeddedUrl(attachment, fileExtension = undefined) {
    const base64Content = attachment.Archivo;
    if (!base64Content) {
      return undefined;
    }

    const currentFileExtension = fileExtension ?? getFileExtensionFromBase64Content(base64Content);
    if (!currentFileExtension) {
      return undefined;
    }

    const mimeType = FILE_EXTENSIONS[currentFileExtension]?.MIME_TYPE;
    if (!mimeType) {
      return undefined;
    }

    return `data:${mimeType};base64,${base64Content}`;
  }

  /**
   * Get the URL of the attachment.
   * @param {Imagen} attachment - The attachment.
   * @param {string} [fileExtension] - The file extension.
   * @returns {string | undefined} The URL of the attachment.
   */
  function getAttachmentUrl(attachment, fileExtension = undefined) {
    return attachment.RutaImagenVinculada
      ? getAttachmentLinkedUrl(attachment)
      : getAttachmentEmbeddedUrl(attachment, fileExtension);
  }

  /**
   * Get the file extension for an attachment.
   *
   * @param {Imagen} attachment - The attachment to open.
   * @returns {string | undefined} The file extension.
   */
  function getAttachmentFileExtension(attachment) {
    if (!attachment.RutaImagenVinculada) {
      const base64Content = attachment.Archivo;
      if (!base64Content) {
        return undefined;
      }

      return getFileExtensionFromBase64Content(base64Content);
    }

    const extensionFromRoute = attachment.RutaImagenVinculada.lastIndexOf('.');
    if (extensionFromRoute === -1) {
      return undefined;
    }

    return attachment.RutaImagenVinculada.substring(extensionFromRoute + 1);
  }

  /**
   * Get all images from attachments.
   * @param {Imagen[]} data - The attachments.
   * @returns {string[]} The images in base64.
   */
  function getAllBase64ImagesFromAttachments(data) {
    return data
      .map((attachment) => {
        const fileExtension = getAttachmentFileExtension(attachment);
        if (fileExtension === 'pdf') {
          return undefined;
        }

        return getAttachmentUrl(attachment, fileExtension);
      })
      .filter((image) => typeof image !== 'undefined');
  }

  /**
   * Opens a report page.
   *
   * @param {Imagen} attachment - The attachment to open.
   * @returns {boolean} `true` if the attachment is opened, `false` otherwise.
   */
  function openReportPage(attachment) {
    /** @type {ArrayBuffer | string | undefined} */
    const url = getAttachmentUrl(attachment);

    if (!url) {
      HiNotification.Toast('No se ha podido obtener la URL del archivo PDF');
      return false;
    }

    f7.views.main.router.navigate('/display-report/', {
      props: {
        reportTitle: attachment.Texto ?? 'Documento Adjunto',
        pdf: url,
      },
    });
    return true;
  }

  /**
   * Opens an attachment.
   *
   * @param {Imagen} attachment - The attachment to open.
   * @param {() => void} [openPdfCallback] - The callback to execute when the attachment is a PDF.
   * @returns {void}
   */
  function openAttachment(attachment, openPdfCallback = undefined) {
    const fileExtension = getAttachmentFileExtension(attachment);
    if (!fileExtension) {
      HiNotification.Toast('Este tipo de archivo no está soportado');
      return;
    }

    const mimeType = FILE_EXTENSIONS[fileExtension]?.MIME_TYPE;
    if (!mimeType) {
      HiNotification.Toast(`No se ha configurado el MIME type para la extensión ".${fileExtension}". Por favor, contacte al administrador`);
      return;
    }

    if (mimeType.includes('image')) {
      const images = getAllBase64ImagesFromAttachments(attachments.value);
      const currentImagePosition = attachments.value.findIndex((a) => a.Id === attachment.Id);
      openPhotoBrowser(images, currentImagePosition);
      return;
    }

    if (fileExtension === 'pdf') {
      const opened = openReportPage(attachment);

      if (opened && typeof openPdfCallback === 'function') {
        openPdfCallback();
      }

      return;
    }

    HiNotification.Toast('Este tipo de archivo no está soportado');
  }

  /**
   * Get the preview of an attachment.
   *
   * @param {Imagen} attachment - The attachment to open.
   * @returns {string | undefined} The preview of the attachment.
   */
  function getAttachmentPreview(attachment) {
    const fileExtension = getAttachmentFileExtension(attachment);
    if (!fileExtension) {
      return undefined;
    }

    if (fileExtension === 'pdf') {
      return PDFIcon;
    }

    return getAttachmentUrl(attachment, fileExtension);
  }

  /**
   * Get the description of an attachment.
   *
   * @param {Imagen} attachment - The attachment.
   * @param {number} docNumber - The number of the document.
   * @returns {string} The description of the attachment.
   */
  function getAttachmentDescription(attachment, docNumber) {
    if (attachment.Texto) {
      return attachment.Texto;
    }

    const fileExtension = getAttachmentFileExtension(attachment);

    if (fileExtension === 'pdf') {
      if (docNumber) {
        return `PDF adjunto ${docNumber}`;
      }
      return 'PDF adjunto';
    }

    return `Imagen ${docNumber}`;
  }

  /**
   * Check if an attachment is a PDF.
   *
   * @param {Imagen} attachment - The attachment.
   * @returns {boolean} `true` if the attachment is a PDF, `false` otherwise.
   */
  function isAttachmentPDF(attachment) {
    return getAttachmentFileExtension(attachment) === 'pdf';
  }

  return {
    attachments,
    isLoadingData,
    loadAttachmentsData,
    openAttachment,
    getAttachmentPreview,
    getAttachmentDescription,
    isAttachmentPDF,
  };
};
